import { ALL_DEPARTMENTS_QUERY } from '@/modules/companies'
import type { TimesheetsSalaryWorkTimeDepartment } from '@/modules/timesheets/types.graphql'
import type { DropdownFilter } from '@/types/graphql/enums'
import { getNodes } from '@/utils/graphql'
import { useQuery } from '@apollo/client'
import type { SelectOption } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import React, { useCallback, useMemo, useState } from 'react'
import type { SelectProps as BaseSelectProps } from './Select'
import { Select } from './Select'

interface DepartmentSelectSelectProps
  extends Omit<
    BaseSelectProps<string, TimesheetsSalaryWorkTimeDepartment>,
    'options'
  > {
  alwaysInclude?: TimesheetsSalaryWorkTimeDepartment
  dropdownPageName?: DropdownFilter
  isDiet?: boolean
  showInManualTimeEntryOvertime?: boolean
  dataset?: TimesheetsSalaryWorkTimeDepartment[]
}

export const DepartmentSelect: React.FC<DepartmentSelectSelectProps> = ({
  alwaysInclude = null,
  dropdownPageName,
  nullable = true,
  dataset,

  // TODO unused?
  isDiet: _isDiet = false,
  showInManualTimeEntryOvertime: _showInManualTimeEntryOvertime = false,

  ...selectProps
}) => {
  const { department: placeholder } = useTranslate({
    department: 'common.department',
  })

  const [query, setQuery] = useState('')
  const { data, loading } = useQuery(ALL_DEPARTMENTS_QUERY)

  const getLabel = useCallback(
    (project: TimesheetsSalaryWorkTimeDepartment) => {
      return <span className="name">{project.name}</span>
    },
    []
  )

  const options = useMemo<
    SelectOption<string, TimesheetsSalaryWorkTimeDepartment>[]
  >(() => {
    if (!data) return []

    const nodes = getNodes(data.allDepartments)

    const options =
      dataset && dataset.length > 0
        ? dataset
            .filter(department =>
              department.name.toLowerCase().includes(query.toLocaleLowerCase())
            )
            .map(department => ({
              value: department.id,
              label: getLabel(department),
              extra: department,
            }))
        : nodes
            .filter(node =>
              node.name.toLowerCase().includes(query.toLowerCase())
            )
            .map(department => ({
              value: department.id,
              label: getLabel(department),
              extra: department,
            }))
    if (alwaysInclude === null) return options

    return [
      {
        value: alwaysInclude.id,
        label: getLabel(alwaysInclude),
        extra: alwaysInclude,
      },
      ...options.filter(option => option.extra.id !== alwaysInclude.id),
    ]
  }, [alwaysInclude, data, dataset, getLabel, query])

  return (
    <Select
      options={options}
      loading={loading}
      placeholder={placeholder}
      searchable={false}
      filterBackground="white"
      optionHoverBackground="gray10"
      onFilterChange={setQuery}
      nullable={nullable}
      {...selectProps}
    />
  )
}
